import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChannelService } from 'src/app/home/channel/channel.service';
import { environment } from 'src/environments/environment';
import { UserService } from '../user/user.service';
import { StoreMode } from 'src/app/core/enums';
import { CartService } from '../cart/cart.service';
import { QrCartService } from '../qr-cart/qr-cart.service';
import { GuestOrderService } from 'src/app/shared/services/guest-order.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionChannelGuard {
  constructor(
    private channelService: ChannelService,
    private router: Router,
    private userService: UserService,
    private cartService: CartService,
    private qrCartService: QrCartService,
    private guestOrderService: GuestOrderService,
  ) { }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const hostParts = window.location.hostname.split('.');
    const isSubdomain = (hostParts.length > 2 && hostParts[0] !== environment.hostName);
    const channelTag = route.paramMap.get('channelTag');

    let subscriptionTag = route.paramMap.get('subscriptionTag');
    if (isSubdomain) {
      subscriptionTag = hostParts[0];
    }

    if (!subscriptionTag || !channelTag) {
      this.router.navigateByUrl('/');
      return false;
    }

    // Guest orders must be scoped even when there is no user/token to compare.
    this.guestOrderService.setSubscription(subscriptionTag, this.channelService.getSubscriptionTag());

    // Always clear cart/qr-cart if their stored subscriptionTag doesn't match the current route
    this.clearCartIfSubscriptionChanged(subscriptionTag);

    this.userService.get(StoreMode.Internal).pipe(take(1)).subscribe((userData: any) => {
      if (userData) {
        let isExpired = false;
        if (userData.accessToken) {
          try {
            if (this.userService.isAccessTokenExpired(userData.accessToken)) {
              isExpired = true;
            }
          } catch (error) {
            isExpired = true;
          }
        }

        if (isExpired || (userData.subscriptionTag && userData.subscriptionTag !== subscriptionTag)) {
          this.userService.logout(true);

          if (localStorage.getItem('cart')) {
            localStorage.removeItem('cart');
            this.cartService.clearCart();
          }

          if (localStorage.getItem('qr-cart')) {
            localStorage.removeItem('qr-cart');
            this.qrCartService.clearCart();
          }

          this.guestOrderService.clear();
        }
      }
    });

    this.channelService.setSubChannel(subscriptionTag, channelTag);

    return true;
  }

  private clearCartIfSubscriptionChanged(subscriptionTag: string): void {
    let cartState = localStorage.getItem('cart');
    let qrCarState = localStorage.getItem('qr-cart');

    if (cartState) {
      let cartStateObj = JSON.parse(cartState);
      let id = cartStateObj['cart']?.ids[0] ?? 'null-my';

      if (cartStateObj['cart']?.entities[id]?.cartSubscriptionTag) {
        let tag = cartStateObj['cart']?.entities[id].cartSubscriptionTag;
        if (tag && tag != subscriptionTag) {
          localStorage.removeItem('cart');
          this.cartService.clearCart();
        }
      }
    }

    if (qrCarState) {
      let qrcartStateObj = JSON.parse(qrCarState);
      let id = qrcartStateObj['qr-cart']?.ids[0] ?? 'null-my';

      if (qrcartStateObj['qr-cart']?.entities[id]?.cartSubscriptionTag) {
        let qrtag = qrcartStateObj['qr-cart']?.entities[id].cartSubscriptionTag;
        if (qrtag && qrtag != subscriptionTag) {
          localStorage.removeItem('qr-cart');
          this.qrCartService.clearCart();
        }
      }
    }
  }
}
